import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Select, SelectItem, Checkbox } from '@nextui-org/react';
import { DateRange } from 'rsuite/DateRangePicker';

import MultiSelectWithActions from '../../common/MultiSelectWithActions';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import {
  getSettlementsEvolutionDataOptimized,
  getAvailableEvolutionFilters,
  type EvolutionFilters,
} from 'common/src/api/settlements';
import {
  SettlementSalesData,
  SettlementMethodology,
  SettlementGroupBy,
  SettlementDateField,
} from '../../../types/settlements';
import { Session } from '@supabase/supabase-js';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { SalesChartTable, SalesExcelButton } from './SalesChartTable';
import { EmptyState } from '../../ui';

interface MarketEvolutionChartProps {
  methodology: SettlementMethodology;
  groupBy: SettlementGroupBy;
  dateField: SettlementDateField;
  dateRange: DateRange | null;
  selectedMarkets: string[];
  session: Session | null;
  userProfile: any;
  selectedSpecies: any;
  selectedSeason: string;
  visualization?: 'chart' | 'table';
}

const MarketEvolutionChart: React.FC<MarketEvolutionChartProps> = ({
  methodology,
  groupBy,
  dateField,
  dateRange,
  selectedMarkets,
  session,
  userProfile,
  selectedSpecies,
  selectedSeason,
  visualization = 'chart',
}) => {
  const { t } = useTranslation('settlementsComparative');
  const { darkMode } = useUserStore();
  // Data and loading states
  const [data, setData] = useState<SettlementSalesData | null>(null);
  const [currency, setCurrency] = useState<string>('USD');
  const [totalKilos, setTotalKilos] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Available filters
  const [availableFilters, setAvailableFilters] = useState<EvolutionFilters>({
    varieties: [],
    calibers: [],
    formats: [],
    colors: [],
  });
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Chart states
  const [selectedMarketsToShow, setSelectedMarketsToShow] = useState<string[]>(
    []
  );
  const [selectedVarieties, setSelectedVarieties] = useState<string[]>([]);
  const [selectedCaliber, setSelectedCaliber] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [showOwnPrices, setShowOwnPrices] = useState(false);

  const ownExporterId = userProfile?.exporter_id?.toString();

  // Check if user is demo
  const isDemo = !!userProfile?.exporter?.hasDemo;

  // Fetch available filters
  const fetchAvailableFilters = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    if (selectedSpecies.id !== 2) {
      return;
    }

    setFiltersLoading(true);

    const startDate = dateRange
      ? dateRange[0]?.toISOString().split('T')[0]
      : undefined;
    const endDate = dateRange
      ? dateRange[1]?.toISOString().split('T')[0]
      : undefined;

    try {
      const filtersData = await getAvailableEvolutionFilters(
        session,
        selectedSeason,
        selectedSpecies.id,
        startDate,
        endDate,
        dateField,
        selectedMarkets.length > 0 ? selectedMarkets : undefined
      );

      setAvailableFilters(filtersData);

      console.log('Market evolution - Available filters loaded:', {
        varieties: filtersData.varieties.length,
        calibers: filtersData.calibers.length,
        formats: filtersData.formats.length,
        colors: filtersData.colors.length,
      });
    } catch (err) {
      console.error('Error fetching available filters:', err);
    } finally {
      setFiltersLoading(false);
    }
  };

  // Fetch evolution data
  const fetchEvolutionData = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    if (selectedSpecies.id !== 2) {
      return;
    }

    setLoading(true);

    const startDate = dateRange
      ? dateRange[0]?.toISOString().split('T')[0]
      : undefined;
    const endDate = dateRange
      ? dateRange[1]?.toISOString().split('T')[0]
      : undefined;

    try {
      console.log('MarketChart API call with parameters:', {
        evolution_group_by: 'market',
        filter_variety:
          selectedVarieties.length > 0 ? selectedVarieties[0] : undefined,
        filter_caliber: selectedCaliber,
        filter_formats: selectedFormats,
        filter_colors: selectedColors,
      });

      const evolutionData = await getSettlementsEvolutionDataOptimized(
        session,
        userProfile.exporter_id,
        methodology,
        selectedSeason,
        'market', // evolution grouping by market
        dateField,
        selectedSpecies.id,
        startDate,
        endDate,
        groupBy,
        selectedMarkets.length > 0 ? selectedMarkets : undefined,
        'none', // separation
        selectedVarieties.length > 0 ? selectedVarieties : undefined,
        selectedCaliber,
        selectedFormats,
        selectedColors
      );

      if (
        evolutionData &&
        typeof evolutionData === 'object' &&
        'data' in evolutionData
      ) {
        // New API response format with currency and total kilos
        setData(evolutionData.data);
        setCurrency(evolutionData.currency || 'USD');
        setTotalKilos(evolutionData.totalKilos || null);
      } else {
        // Fallback for old format
        setData(evolutionData);
        setCurrency('USD');
        setTotalKilos(null);
      }
      console.log('Market evolution data received:', {
        dataKeys: Object.keys(evolutionData || {}),
        sampleData: evolutionData ? Object.keys(evolutionData)[0] : null,
        allKeys: evolutionData ? Object.keys(evolutionData) : [],
      });
    } catch (err) {
      console.error('Error fetching evolution data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filters on mount and when dependencies change
  useEffect(() => {
    fetchAvailableFilters();
  }, [selectedMarkets, dateRange, dateField, selectedSeason, selectedSpecies]);

  // Auto-select first options when available filters change
  useEffect(() => {
    if (
      availableFilters.varieties.length > 0 &&
      selectedVarieties.length === 0
    ) {
      if (isDemo) {
        // For demo users, only select LAPINS if available
        const lapinsVariety = availableFilters.varieties.find(
          (v) => v.value.toUpperCase() === 'LAPINS'
        );
        if (lapinsVariety) {
          console.log('Market chart - Auto-selecting LAPINS for demo user');
          setSelectedVarieties([lapinsVariety.value]);
        }
      } else {
        console.log(
          'Market chart - Auto-selecting varieties from useEffect:',
          availableFilters.varieties.map((v) => v.value)
        );
        setSelectedVarieties(availableFilters.varieties.map((v) => v.value));
      }
    }
    if (availableFilters.calibers.length > 0 && !selectedCaliber) {
      console.log(
        'Market chart - Auto-selecting caliber from useEffect:',
        availableFilters.calibers[0].value
      );
      setSelectedCaliber(availableFilters.calibers[0].value);
    }
    if (availableFilters.formats.length > 0 && selectedFormats.length === 0) {
      console.log(
        'Market chart - Auto-selecting formats from useEffect:',
        availableFilters.formats.map((f) => f.value)
      );
      setSelectedFormats(availableFilters.formats.map((f) => f.value));
    }
    if (availableFilters.colors.length > 0 && selectedColors.length === 0) {
      console.log(
        'Market chart - Auto-selecting colors from useEffect:',
        availableFilters.colors.map((c) => c.value)
      );
      setSelectedColors(availableFilters.colors.map((c) => c.value));
    }
  }, [availableFilters, isDemo]);

  // Auto-select markets when data loads
  useEffect(() => {
    if (data && selectedMarketsToShow.length === 0) {
      const availableMarkets = Object.keys(data);
      const firstMarkets = availableMarkets.slice(0, 3);
      console.log('Market chart - Auto-selecting markets:', firstMarkets);
      setSelectedMarketsToShow(firstMarkets);
    }
  }, [data]);

  // Fetch data when filters change
  useEffect(() => {
    if (
      selectedVarieties.length > 0 &&
      selectedCaliber &&
      selectedColors.length > 0 &&
      selectedFormats.length > 0
    ) {
      console.log('Market chart - Fetching evolution data with filters:', {
        selectedVarieties: selectedVarieties.length,
        selectedCaliber,
        selectedColors: selectedColors.length,
        selectedFormats: selectedFormats.length,
      });
      fetchEvolutionData();
    }
  }, [
    methodology,
    groupBy,
    selectedMarkets,
    dateRange,
    dateField,
    selectedVarieties,
    selectedCaliber,
    selectedColors,
    selectedFormats,
  ]);

  const processedData = useMemo((): SettlementSalesData | null => {
    if (!data) {
      return null;
    }

    return data as SettlementSalesData;
  }, [data, methodology, ownExporterId]);

  // Extract market names mapping if available
  const marketNames = useMemo(() => {
    if (!data || !('_marketNames' in data)) return {};
    return (data as any)._marketNames || {};
  }, [data]);

  // Convert market IDs to display names for the UI
  const availableMarketNames = useMemo(() => {
    if (!data) return [];
    return Object.keys(data).map((marketId) => {
      const marketName = marketNames[marketId];
      return {
        id: marketId,
        name: marketName || `Mercado ${marketId}`,
      };
    });
  }, [data, marketNames]);

  const prepareMarketEvolutionChartData = (
    chartData: SettlementSalesData | null,
    marketsFilter: string[]
  ) => {
    console.log('Market chart - Preparing chart data:', {
      hasChartData: !!chartData,
      chartDataKeys: chartData ? Object.keys(chartData) : [],
      marketsFilterLength: marketsFilter.length,
      marketsFilter: marketsFilter.slice(0, 3), // show first 3 for debugging
    });

    if (!chartData) {
      return { categories: [], series: [] };
    }

    const marketsToShow =
      marketsFilter.length > 0 ? marketsFilter : Object.keys(chartData);

    console.log('Markets to show:', marketsToShow);

    // Get all date keys from all markets
    const allDateKeys = new Set<string>();
    marketsToShow.forEach((marketId) => {
      if (chartData[marketId]) {
        Object.keys(chartData[marketId]).forEach((dateKey) =>
          allDateKeys.add(dateKey)
        );
      } else {
        console.log(`Market ${marketId} not found in chartData`);
      }
    });

    const dateKeys = Array.from(allDateKeys).sort();
    console.log(
      'Date keys found:',
      dateKeys.length,
      'first few:',
      dateKeys.slice(0, 3)
    );

    // Create series for each market
    const series: any[] = [];

    marketsToShow.forEach((marketId, index) => {
      const marketName = marketNames[marketId] || `Mercado ${marketId}`;
      const baseColor = `hsl(${
        index * (360 / marketsToShow.length)
      }, 70%, 50%)`;
      const lightColor = `hsl(${
        index * (360 / marketsToShow.length)
      }, 70%, 80%)`;

      const marketData = chartData[marketId];
      if (!marketData) return;

      // Own data series - only include if showOwnPrices is true
      if (showOwnPrices) {
        const ownData: Array<{ x: string; y: number | null }> = [];

        dateKeys.forEach((dateKey) => {
          const dateData = marketData[dateKey];
          let ownPrice = null;

          if (dateData) {
            // Aggregate all market entries for this date
            let ownTotalKilos = 0;
            let ownTotalSales = 0;

            Object.values(dateData).forEach((marketEntryData: any) => {
              // Calculate own aggregated price
              if (
                marketEntryData.own &&
                Array.isArray(marketEntryData.own) &&
                marketEntryData.own.length > 0 &&
                marketEntryData.ownKg &&
                marketEntryData.ownKg > 0
              ) {
                const ownValue = marketEntryData.own[0]?.value;
                if (ownValue && typeof ownValue === 'number') {
                  ownTotalKilos += marketEntryData.ownKg;
                  ownTotalSales += ownValue * marketEntryData.ownKg;
                }
              }
            });

            // Calculate weighted average prices
            if (ownTotalKilos > 0) {
              ownPrice = ownTotalSales / ownTotalKilos;
            }
          }

          ownData.push({ x: dateKey, y: ownPrice });
        });

        // Add own series (solid line, lighter color)
        series.push({
          name: `${marketName} - ${t('Tu Exportadora')}`,
          data: ownData,
          color: lightColor,
          type: 'line',
          stroke: {
            width: 3,
            dashArray: 0, // Solid line
          },
        });
      }

      // Others data series - always included
      const othersData: Array<{ x: string; y: number | null }> = [];

      dateKeys.forEach((dateKey) => {
        const dateData = marketData[dateKey];
        let othersPrice = null;

        if (dateData) {
          // Aggregate all market entries for this date
          let othersTotalKilos = 0;
          let othersTotalSales = 0;

          Object.values(dateData).forEach((marketEntryData: any) => {
            // Calculate others aggregated price (using median)
            if (
              marketEntryData.others &&
              marketEntryData.others.median &&
              marketEntryData.others.median > 0 &&
              marketEntryData.othersKg &&
              marketEntryData.othersKg > 0
            ) {
              othersTotalKilos += marketEntryData.othersKg;
              othersTotalSales +=
                marketEntryData.others.median * marketEntryData.othersKg;
            }
          });

          // Calculate weighted average prices
          if (othersTotalKilos > 0) {
            othersPrice = othersTotalSales / othersTotalKilos;
          }
        }

        othersData.push({ x: dateKey, y: othersPrice });
      });

      // Add others series (dashed line, base color)
      series.push({
        name: `${marketName} - ${
          methodology === 'by_volume' ? t('Mercado') : t('Otras Exportadoras')
        }`,
        data: othersData,
        color: baseColor,
        type: 'line',
        stroke: {
          width: 2,
          dashArray: 0, // Solid line
        },
      });
    });

    return {
      categories: dateKeys,
      series: series,
    };
  };

  const chartData = useMemo(
    () => prepareMarketEvolutionChartData(processedData, selectedMarketsToShow),
    [processedData, selectedMarketsToShow, showOwnPrices, marketNames]
  );

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        height: 400,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        zoom: {
          enabled: true,
        },
        toolbar: {
          show: true,
        },
      },
      stroke: {
        curve: 'smooth',
        width: chartData.series.map((s) => s.stroke?.width || 2),
        dashArray: chartData.series.map((s) => s.stroke?.dashArray || 0),
      },
      xaxis: {
        categories: chartData.categories,
        title: {
          text: groupBy === 'week' ? t('Semana') : t('Día'),
        },
        labels: {
          formatter: (value) => {
            if (typeof value !== 'string') {
              return value;
            }
            if (groupBy === 'week') {
              const parts = value.split('-W');
              return parts.length > 1 ? parts[1] : value;
            } else {
              const parts = value.split('-');
              if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}`;
              }
              return value;
            }
          },
          rotate: -45,
        },
      },
      yaxis: {
        title: {
          text: `${t('Precio')} (${currency})`,
        },
        labels: {
          formatter: (value) => {
            if (!value) return '';
            const symbol = currency === 'RMB' ? '¥' : 'USD';
            return `${value.toFixed(2)} ${symbol}`;
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value) =>
            value ? `$${value.toFixed(2)}/kg` : t('Sin datos'),
        },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
      },
      colors: chartData.series.map((s) => s.color),
    }),
    [chartData.categories, chartData.series, groupBy]
  );

  // Show loading state
  if (loading || filtersLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-default-400'>
          {filtersLoading
            ? t('Cargando filtros...')
            : t('Cargando datos de evolución...')}
        </p>
      </div>
    );
  }

  // Show no data state
  if (!data) {
    return (
      <EmptyState title={t('No hay datos de evolución disponibles')} />
    );
  }

  return (
    <div className='w-full'>
      {/* Chart Title */}
      <div className='mb-4'>
        <div className='flex items-start justify-between gap-3'>
          <h3 className='text-lg font-semibold'>
            {t('Evolución de Precios por Mercado')}
          </h3>
          <SalesExcelButton
            categories={chartData.categories}
            series={chartData.series}
            currency={currency}
            fileName={`precios_evolucion_mercado_${selectedCaliber || 'data'}.xlsx`}
            label={t('Exportar a Excel')}
          />
        </div>
        <p className='text-default-500 text-sm'>
          {t('Moneda')}: {currency}
        </p>
        {totalKilos && (
          <p className='text-default-500 text-sm mt-1'>
            {t('Muestra')}: {totalKilos.toLocaleString('es-CL')} kg
          </p>
        )}
      </div>

      {/* Specific Filters Section */}
      <div className='grid grid-cols-1 md:grid-cols-5 gap-4 mb-6'>
        <MultiSelectWithActions
          label={t('Mercados a mostrar')}
          placeholder={t('Selecciona mercados')}
          options={availableMarketNames.map((market) => ({
            value: market.id,
            label: market.name,
          }))}
          selectedKeys={selectedMarketsToShow}
          onSelectionChange={setSelectedMarketsToShow}
          size='md'
        />

        <MultiSelectWithActions
          label={isDemo ? t('Variedad (Demo: Solo Lapins)') : t('Variedad')}
          placeholder={
            isDemo
              ? t('Demo: Solo Lapins disponible')
              : t('Selecciona variedades')
          }
          options={(isDemo
            ? availableFilters.varieties.filter(
                (v) => v.value.toUpperCase() === 'LAPINS'
              )
            : availableFilters.varieties
          ).map((variety) => ({
            value: variety.value,
            label: variety.value,
          }))}
          selectedKeys={selectedVarieties}
          onSelectionChange={(keys) => {
            if (!isDemo) {
              setSelectedVarieties(keys);
            }
          }}
          size='md'
          isDisabled={isDemo}
        />

        <Select
          label={t('Calibre')}
          placeholder={t('Selecciona un calibre')}
          selectedKeys={
            selectedCaliber ? new Set([selectedCaliber]) : new Set()
          }
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as string;
            if (selected) setSelectedCaliber(selected);
          }}
          size='md'
        >
          {availableFilters.calibers.map((caliber) => (
            <SelectItem
              key={caliber.value}
              value={caliber.value}
              textValue={caliber.value}
            >
              {caliber.value}
            </SelectItem>
          ))}
        </Select>

        <MultiSelectWithActions
          label={t('Formato')}
          placeholder={t('Selecciona formatos')}
          options={availableFilters.formats.map((format) => ({
            value: format.value,
            label: format.value,
          }))}
          selectedKeys={selectedFormats}
          onSelectionChange={setSelectedFormats}
          size='md'
        />

        <MultiSelectWithActions
          label={t('Color')}
          placeholder={t('Selecciona colores')}
          options={availableFilters.colors.map((color) => ({
            value: color.value,
            label: color.value,
          }))}
          selectedKeys={selectedColors}
          onSelectionChange={setSelectedColors}
          size='md'
        />
      </div>

      {/* Chart Controls */}
      <div className='flex items-center justify-end mb-6'>
        <Checkbox
          isSelected={showOwnPrices}
          onValueChange={setShowOwnPrices}
          size='md'
          color='primary'
        >
          {t('Mostrar mis precios')}
        </Checkbox>
      </div>

      <div className=''>
        {visualization === 'table' ? (
          <SalesChartTable
            categories={chartData.categories}
            series={chartData.series}
            currency={currency}
            emptyLabel={t(
              'No hay datos disponibles para los mercados seleccionados'
            )}
          />
        ) : chartData.series.length > 0 ? (
          <ReactApexChart
            options={chartOptions}
            series={chartData.series}
            type='line'
            height={400}
          />
        ) : (
          <div className='flex items-center justify-center h-64'>
            <p className='text-default-400'>
              {t('No hay datos disponibles para los mercados seleccionados')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketEvolutionChart;
